/**
 * Analytics events for Google Tag Manager.
 *
 * Events go to `window.dataLayer`; GTM forwards them to GA4 with a single
 * "GA4 Event" tag. The full list of events and parameters is in docs/ANALYTICS.md.
 */

import type { TtsLanguage } from '~/types'

type EntityId = string | number

export type AnalyticsLeadType = 'credit' | 'deposit' | 'card' | 'b2b_credit' | 'org_contact'
export type AnalyticsAuthMethod = 'phone' | 'google' | 'telegram'

export interface AnalyticsEvents {
  generate_lead: {
    lead_type: AnalyticsLeadType
    product_id?: EntityId | null
    organization_id?: EntityId | null
    organization_type?: string | null
  }
  lead_form_error: { lead_type: AnalyticsLeadType }
  lead_cta_click: { cta_location: string }
  sign_up: { method: AnalyticsAuthMethod }
  login: { method: AnalyticsAuthMethod }
  contact_form_submit: { form_type: 'contact' | 'question' }
  add_to_compare: { product_type: string; product_id: EntityId }
  share: { method: string; content_type: string }
  submit_review: { rateable_type: string; product_id: EntityId }
  rate_alert_subscribe: { currency: string }
  ai_chat_message: Record<string, never>
  phone_click: { link_url: string }
  quiz_complete: { quiz_id: EntityId }
  deposit_selection_complete: Record<string, never>
  insurance_widget_open: { insurance_type: string }
  partner_link_click: { partner: string; link_location: string }
  tts_play: { tts_language: TtsLanguage; tts_provider: 'airun' | 'browser' }
}

export type AnalyticsEventName = keyof AnalyticsEvents

// Every parameter any event may carry. GTM merges dataLayer pushes into one model,
// so these keys are reset before each event to keep a previous event's values
// from leaking into the next one.
const EVENT_PARAM_KEYS = [
  'lead_type',
  'product_id',
  'product_type',
  'organization_id',
  'organization_type',
  'cta_location',
  'method',
  'form_type',
  'content_type',
  'rateable_type',
  'currency',
  'link_url',
  'quiz_id',
  'insurance_type',
  'partner',
  'link_location',
  'tts_language',
  'tts_provider'
] as const

const pushToDataLayer = (payload: Record<string, unknown>) => {
  if (!import.meta.client) return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

export const useAnalytics = () => {
  const trackEvent = <T extends AnalyticsEventName>(event: T, params: AnalyticsEvents[T]) => {
    const resetParams = Object.fromEntries(EVENT_PARAM_KEYS.map((key) => [key, undefined]))

    pushToDataLayer(resetParams)
    pushToDataLayer({ event, ...params })
  }

  const setUserId = (userId: string | null) => {
    pushToDataLayer({ user_id: userId ?? undefined })
  }

  return { trackEvent, setUserId }
}
